import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { X, RefreshCw } from '@/ui/icons'
import type {
  GradeCategory,
  GradeSegment,
  ElevationSample,
  RouteGeometryData,
  RouteGeometryPoi,
  RouteGeometryWaypoint,
} from '../../hooks/useRouteGeometry'
import { TripRouteElevationChart } from './TripRouteElevationChart'

import {
  haversineMeters,
  cumulativeDistances,
  interpolateAlongLine,
} from '../../utils/routeGeo'
import { computeCameraStep } from '../../utils/tourCameraDriver'

const STYLE_URL = 'mapbox://styles/ayott/cmdldmyxv00eu01ptcd7acb7k'
const ROUTE_CASING_COLOR = '#000000'
const MARKER_START_COLOR = '#22c55e'
const MARKER_END_COLOR = '#5b8fbe'
const MARKER_MID_COLOR = '#6b7280'

const GRADE_COLOR: Record<GradeCategory, string> = {
  easy: '#22c55e',
  moderate: '#eab308',
  steep: '#f97316',
  extreme: '#ef4444',
}

const POI_COLOR: Record<string, string> = {
  gas: '#dc2626',
  water: '#0ea5e9',
  campsite: '#16a34a',
  viewpoint: '#eab308',
  meetup_spot: '#6366f1',
  trailhead: '#737373',
  custom: '#737373',
}

const POI_ICON: Record<string, string> = {
  gas: '⛽',
  water: '💧',
  campsite: '⛺',
  viewpoint: '👁',
  meetup_spot: '📍',
  trailhead: '🥾',
  custom: '📌',
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v))
}

function nearestCoordIdx(coords: [number, number][], target: [number, number]): number {
  let best = 0
  let bestDist = Infinity
  for (let i = 0; i < coords.length; i++) {
    const d = haversineMeters(coords[i], target)
    if (d < bestDist) {
      bestDist = d
      best = i
    }
  }
  return best
}

function buildGradeSegmentsFromProfile(profile: ElevationSample[]): GradeSegment[] {
  if (profile.length < 2) return []
  const cat = (g: number): GradeCategory => {
    const a = Math.abs(g)
    if (a < 5) return 'easy'
    if (a < 10) return 'moderate'
    if (a < 15) return 'steep'
    return 'extreme'
  }
  const segs: GradeSegment[] = []
  let startIdx = 0
  let current: GradeCategory | null = null
  let sum = 0
  let count = 0
  for (let i = 1; i < profile.length; i++) {
    const dd = profile[i].distanceMeters - profile[i - 1].distanceMeters
    if (dd <= 0) continue
    const de = profile[i].elevationMeters - profile[i - 1].elevationMeters
    const g = (de / dd) * 100
    const c = cat(g)
    if (current === null) {
      current = c
      startIdx = i - 1
      sum = g
      count = 1
    } else if (c === current) {
      sum += g
      count++
    } else {
      segs.push({ startIdx, endIdx: i - 1, grade: count ? sum / count : 0, category: current })
      current = c
      startIdx = i - 1
      sum = g
      count = 1
    }
  }
  if (current !== null) {
    segs.push({ startIdx, endIdx: profile.length - 1, grade: count ? sum / count : 0, category: current })
  }
  return segs
}

interface DayView {
  coords: [number, number][]
  cumDists: number[]
  totalDistM: number
  profile: ElevationSample[]
  gradeSegments: GradeSegment[]
  pois: RouteGeometryPoi[]
  waypoints: RouteGeometryWaypoint[]
}

function deriveDayView(geometry: RouteGeometryData, selectedDay: number | 'all'): DayView {
  const fullCum = cumulativeDistances(geometry.coordinates)
  const full: DayView = {
    coords: geometry.coordinates,
    cumDists: fullCum,
    totalDistM: fullCum[fullCum.length - 1] ?? 0,
    profile: geometry.elevationProfile,
    gradeSegments: geometry.gradeSegments,
    pois: geometry.pois,
    waypoints: geometry.waypoints,
  }
  if (selectedDay === 'all') return full

  const dayWps = geometry.waypoints.filter((w) => w.tripDay === selectedDay)
  if (dayWps.length < 1) return full

  const indices = dayWps.map((wp) =>
    nearestCoordIdx(geometry.coordinates, [wp.longitude, wp.latitude]),
  )
  const startIdx = Math.max(0, Math.min(...indices))
  const endIdx = Math.min(geometry.coordinates.length - 1, Math.max(...indices))
  if (endIdx - startIdx < 1) return full

  const coords = geometry.coordinates.slice(startIdx, endIdx + 1)
  const cumDists = cumulativeDistances(coords)
  const totalDistM = cumDists[cumDists.length - 1] ?? 0

  const startDist = fullCum[startIdx] ?? 0
  const endDist = fullCum[endIdx] ?? 0
  const profile: ElevationSample[] = geometry.elevationProfile
    .filter((s) => s.distanceMeters >= startDist && s.distanceMeters <= endDist)
    .map((s) => ({
      distanceMeters: s.distanceMeters - startDist,
      elevationMeters: s.elevationMeters,
      coordIdx: Math.max(0, Math.min(coords.length - 1, s.coordIdx - startIdx)),
    }))
  const gradeSegments = buildGradeSegmentsFromProfile(profile)
  const pois = geometry.pois.filter((p) => p.tripDay === selectedDay || p.tripDay == null)

  return { coords, cumDists, totalDistM, profile, gradeSegments, pois, waypoints: dayWps }
}

function buildGradeFeatureCollection(
  coords: [number, number][],
  profile: ElevationSample[],
  gradeSegments: GradeSegment[],
): GeoJSON.FeatureCollection<GeoJSON.LineString> {
  if (gradeSegments.length === 0 || coords.length < 2) {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { category: 'easy' },
          geometry: { type: 'LineString', coordinates: coords },
        },
      ],
    }
  }
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = []
  for (const seg of gradeSegments) {
    const startCoord = profile[seg.startIdx]?.coordIdx
    const endCoord = profile[seg.endIdx]?.coordIdx
    if (startCoord == null || endCoord == null) continue
    const s = Math.max(0, Math.min(coords.length - 1, startCoord))
    const e = Math.max(0, Math.min(coords.length - 1, endCoord))
    if (e - s < 1) continue
    features.push({
      type: 'Feature',
      properties: { category: seg.category },
      geometry: { type: 'LineString', coordinates: coords.slice(s, e + 1) },
    })
  }
  return { type: 'FeatureCollection', features }
}

const SPEEDS = [0.5, 1, 2, 4] as const
type Speed = (typeof SPEEDS)[number]
const FRAME_MS = 50
const EASE_DURATION_MS = 350
const TERRAIN_SOURCE_ID = 'mapbox-dem-player'
const ROUTE_SOURCE_ID = 'route-player'
const WAYPOINTS_SOURCE_ID = 'waypoints-player'
const CURSOR_SOURCE_ID = 'route-cursor-player'

interface Props {
  geometry: RouteGeometryData
  onClose: () => void
}

export function TripRoute3DPlayerModal({ geometry, onClose }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const poiMarkersRef = useRef<mapboxgl.Marker[]>([])
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const progressRef = useRef(0)

  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [speed, setSpeed] = useState<Speed>(1)
  const [mapReady, setMapReady] = useState(false)

  const dayOptions = useMemo(() => {
    const set = new Set<number>()
    for (const wp of geometry.waypoints) {
      if (typeof wp.tripDay === 'number') set.add(wp.tripDay)
    }
    return Array.from(set).sort((a, b) => a - b)
  }, [geometry.waypoints])

  const [selectedDay, setSelectedDay] = useState<number | 'all'>('all')

  const dayView = useMemo(() => deriveDayView(geometry, selectedDay), [geometry, selectedDay])
  const coords = dayView.coords

  const cumDists = useRef<number[]>(dayView.cumDists)
  const totalDistM = useRef(dayView.totalDistM)
  useEffect(() => {
    cumDists.current = dayView.cumDists
    totalDistM.current = dayView.totalDistM
  }, [dayView])

  const baseDurationSec = clamp((dayView.totalDistM / 1000) * 30, 60, 300)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    if (coords.length < 2) return

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN

    const mid = coords[Math.floor(coords.length / 2)]

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: [mid[0], mid[1]],
      zoom: 10,
      pitch: 45,
      bearing: 0,
      attributionControl: true,
    })
    mapRef.current = map

    map.on('load', () => {
      try {
        map.addSource(TERRAIN_SOURCE_ID, {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        })
        map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: 1.4 })
      } catch (_) {}

      try {
        map.addLayer({
          id: 'sky-player',
          type: 'sky',
          paint: {
            'sky-type': 'atmosphere',
            'sky-atmosphere-sun': [0.0, 0.0],
            'sky-atmosphere-sun-intensity': 15,
          } as mapboxgl.SkyPaint,
        })
      } catch (_) {}

      const fc = buildGradeFeatureCollection(coords, dayView.profile, dayView.gradeSegments)
      map.addSource(ROUTE_SOURCE_ID, { type: 'geojson', data: fc })
      map.addLayer({
        id: 'route-player-casing',
        type: 'line',
        source: ROUTE_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ROUTE_CASING_COLOR, 'line-width': 9, 'line-opacity': 0.55 },
      })
      map.addLayer({
        id: 'route-player-line',
        type: 'line',
        source: ROUTE_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': [
            'match',
            ['get', 'category'],
            'easy',
            GRADE_COLOR.easy,
            'moderate',
            GRADE_COLOR.moderate,
            'steep',
            GRADE_COLOR.steep,
            'extreme',
            GRADE_COLOR.extreme,
            GRADE_COLOR.easy,
          ],
          'line-width': 5,
        },
      })

      const waypointFeatures: GeoJSON.Feature<GeoJSON.Point>[] = dayView.waypoints.map((wp, i) => ({
        type: 'Feature',
        properties: {
          color:
            i === 0
              ? MARKER_START_COLOR
              : i === dayView.waypoints.length - 1
                ? MARKER_END_COLOR
                : MARKER_MID_COLOR,
        },
        geometry: { type: 'Point', coordinates: [wp.longitude, wp.latitude] },
      }))
      map.addSource(WAYPOINTS_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: waypointFeatures },
      })
      map.addLayer({
        id: 'waypoints-player-stroke',
        type: 'circle',
        source: WAYPOINTS_SOURCE_ID,
        paint: { 'circle-radius': 9, 'circle-color': '#ffffff' },
      })
      map.addLayer({
        id: 'waypoints-player-fill',
        type: 'circle',
        source: WAYPOINTS_SOURCE_ID,
        paint: { 'circle-radius': 7, 'circle-color': ['get', 'color'] },
      })

      renderPois(map, dayView.pois)

      // Chart cursor — moving marker that shows where the scrubber maps onto the route.
      const startPos = coords[0]
      map.addSource(CURSOR_SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Point', coordinates: startPos },
        },
      })
      map.addLayer({
        id: 'route-cursor-halo',
        type: 'circle',
        source: CURSOR_SOURCE_ID,
        paint: {
          'circle-radius': 14,
          'circle-color': '#ffffff',
          'circle-opacity': 0.25,
        },
      })
      map.addLayer({
        id: 'route-cursor-stroke',
        type: 'circle',
        source: CURSOR_SOURCE_ID,
        paint: { 'circle-radius': 8, 'circle-color': '#ffffff' },
      })
      map.addLayer({
        id: 'route-cursor-fill',
        type: 'circle',
        source: CURSOR_SOURCE_ID,
        paint: { 'circle-radius': 5, 'circle-color': '#5b8fbe' },
      })

      map.flyTo({ center: [startPos[0], startPos[1]], zoom: 13, pitch: 55, bearing: 0, duration: 800 })

      setMapReady(true)
    })

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      poiMarkersRef.current.forEach((m) => m.remove())
      poiMarkersRef.current = []
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialize once
  }, [])

  // Update route + waypoints + POIs when day selection changes
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    const fc = buildGradeFeatureCollection(coords, dayView.profile, dayView.gradeSegments)
    const routeSrc = map.getSource(ROUTE_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (routeSrc) routeSrc.setData(fc)

    const wpFeatures: GeoJSON.Feature<GeoJSON.Point>[] = dayView.waypoints.map((wp, i) => ({
      type: 'Feature',
      properties: {
        color:
          i === 0
            ? MARKER_START_COLOR
            : i === dayView.waypoints.length - 1
              ? MARKER_END_COLOR
              : MARKER_MID_COLOR,
      },
      geometry: { type: 'Point', coordinates: [wp.longitude, wp.latitude] },
    }))
    const wpSrc = map.getSource(WAYPOINTS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (wpSrc) wpSrc.setData({ type: 'FeatureCollection', features: wpFeatures })

    renderPois(map, dayView.pois)

    // Refit bounds
    if (coords.length >= 2) {
      const bounds = coords.reduce(
        (b, c) => b.extend(c as [number, number]),
        new mapboxgl.LngLatBounds(coords[0], coords[0]),
      )
      map.fitBounds(bounds, { padding: 64, pitch: 45, bearing: 0, duration: 800 })
    }

    progressRef.current = 0
    setProgress(0)
    setIsPlaying(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Snap the cursor marker to the (new) day's start.
    if (coords.length > 0) {
      const cursorSrc = map.getSource(CURSOR_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
      cursorSrc?.setData({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: coords[0] },
      })
    }
  }, [dayView, mapReady, coords])

  function renderPois(map: mapboxgl.Map, pois: RouteGeometryPoi[]) {
    poiMarkersRef.current.forEach((m) => m.remove())
    poiMarkersRef.current = []
    for (const poi of pois) {
      const el = document.createElement('div')
      el.className = 'poi-marker-3d'
      const color = POI_COLOR[poi.poiType ?? 'custom'] ?? POI_COLOR.custom
      const icon = POI_ICON[poi.poiType ?? 'custom'] ?? POI_ICON.custom
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: ${color}; color: white;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; box-shadow: 0 0 0 2px white, 0 2px 6px rgba(0,0,0,0.5);
        cursor: default; user-select: none;
      `
      el.textContent = icon
      el.title = poi.name
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(poi.coordinates)
        .addTo(map)
      poiMarkersRef.current.push(marker)
    }
  }

  const updateCursor = useCallback((pos: [number, number]) => {
    const map = mapRef.current
    if (!map) return
    const src = map.getSource(CURSOR_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (!src) return
    src.setData({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: pos },
    })
  }, [])

  const tick = useCallback(() => {
    const map = mapRef.current
    const cd = cumDists.current
    if (!map || cd.length < 2) return

    const durationSec = baseDurationSec / speed
    const stepPerTick = FRAME_MS / 1000 / durationSec

    progressRef.current = Math.min(progressRef.current + stepPerTick, 1)
    setProgress(progressRef.current)

    const step = computeCameraStep(progressRef.current, {
      coords,
      cumDists: cd,
      totalDistM: totalDistM.current,
      waypoints: dayView.waypoints,
    })
    updateCursor(step.cursorPos)
    map.easeTo({
      center: step.center,
      zoom: step.zoom,
      pitch: step.pitch,
      bearing: step.bearing,
      duration: EASE_DURATION_MS,
    })

    if (progressRef.current >= 1) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = null
      setIsPlaying(false)
    }
  }, [coords, speed, baseDurationSec, dayView.waypoints, updateCursor])

  const play = useCallback(() => {
    if (progressRef.current >= 1) {
      progressRef.current = 0
      setProgress(0)
    }
    setIsPlaying(true)
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(tick, FRAME_MS)
  }, [tick])

  const pause = useCallback(() => {
    setIsPlaying(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const restart = useCallback(() => {
    pause()
    progressRef.current = 0
    setProgress(0)
    const map = mapRef.current
    if (map && coords.length > 0) {
      const startPos = coords[0]
      updateCursor(startPos)
      map.flyTo({ center: [startPos[0], startPos[1]], zoom: 13, pitch: 55, bearing: 0, duration: 600 })
    }
  }, [pause, coords, updateCursor])

  useEffect(() => {
    if (isPlaying) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      intervalRef.current = setInterval(tick, FRAME_MS)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [isPlaying, tick])

  const onSeek = useCallback(
    (t: number) => {
      progressRef.current = t
      setProgress(t)
      if (coords.length < 2) return
      const pos = interpolateAlongLine(coords, cumDists.current, t)
      updateCursor(pos)
      mapRef.current?.easeTo({ center: pos, duration: 200 })
    },
    [coords, updateCursor],
  )

  const cycleSpeed = useCallback(() => {
    setSpeed((s) => {
      const idx = SPEEDS.indexOf(s)
      return SPEEDS[(idx + 1) % SPEEDS.length]
    })
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const showDaySelector = dayOptions.length > 1

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      role="dialog"
      aria-label="3D Route Tour"
      aria-modal
    >
      <div ref={containerRef} className="flex-1 w-full" />

      {!mapReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span className="text-white/70 text-sm">Loading 3D tour…</span>
          </div>
        </div>
      )}

      {/* Top bar: day selector + grade legend.
          Left offset clears the app's left navbar (~96px). */}
      <div className="absolute top-4 left-28 right-16 z-10 flex flex-wrap items-center gap-3 pointer-events-none">
        {showDaySelector && (
          <div className="flex items-center gap-1 bg-black/60 rounded-full px-1 py-1 pointer-events-auto">
            <button
              onClick={() => setSelectedDay('all')}
              className={`text-xs px-3 py-1 rounded-full transition-colors ${
                selectedDay === 'all'
                  ? 'bg-white text-black'
                  : 'text-white/80 hover:bg-white/15'
              }`}
            >
              All days
            </button>
            {dayOptions.map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDay(d)}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${
                  selectedDay === d
                    ? 'bg-white text-black'
                    : 'text-white/80 hover:bg-white/15'
                }`}
              >
                Day {d}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 bg-black/60 rounded-full px-3 py-1 pointer-events-auto">
          <span className="text-[10px] uppercase tracking-wide text-white/60">Grade</span>
          {(['easy', 'moderate', 'steep', 'extreme'] as GradeCategory[]).map((cat) => (
            <span key={cat} className="flex items-center gap-1 text-[11px] text-white/80">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: GRADE_COLOR[cat] }}
              />
              {cat === 'easy'
                ? '<5%'
                : cat === 'moderate'
                  ? '5–10%'
                  : cat === 'steep'
                    ? '10–15%'
                    : '15%+'}
            </span>
          ))}
        </div>
      </div>

      {/* Top-right actions: close. */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
          aria-label="Close 3D tour"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Bottom HUD: elevation chart + controls. Left offset clears the app navbar. */}
      <div className="absolute bottom-0 left-28 right-4 pb-4 pt-6 bg-gradient-to-t from-black/85 to-transparent pointer-events-none">
        <div className="pointer-events-auto mb-3">
          <TripRouteElevationChart
            profile={dayView.profile}
            gradeSegments={dayView.gradeSegments}
            progress={progress}
            onSeek={onSeek}
          />
        </div>

        <div className="flex items-center justify-center gap-3 pointer-events-auto">
          <button
            onClick={restart}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors"
            aria-label="Restart"
          >
            <RefreshCw className="h-4 w-4" />
          </button>

          <button
            onClick={isPlaying ? pause : play}
            disabled={!mapReady}
            className="w-14 h-14 flex items-center justify-center rounded-full bg-white text-black shadow-lg hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg className="h-6 w-6 translate-x-0.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
          </button>

          <button
            onClick={cycleSpeed}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors text-xs font-bold"
            aria-label={`Speed: ${speed}×`}
          >
            {speed}×
          </button>
        </div>
      </div>
    </div>
  )
}
